import axios from 'utils/axios';
import type {
    GetProductListResultType,
    ProductImportResponseType,
    ProductImportRowType,
    ProductCreateDataType,
    ProductSaveDataType,
    ProductType,
} from '@econtractor/types';

export interface ProductListParams {
    page?: number;
    search?: string;
    order?: string;
    status?: number[];
    onPage?: number;
    Ids?: number[];
}

export interface ProductSaveResponse {
    stat?: boolean | number;
    articleDublicate?: boolean;
}

export interface VendorActionResponse {
    stat: boolean;
    vendorId?: number;
    vendor_name?: string;
    errCode?: 'EMPTY_NAME' | 'DUPLICATE_NAME' | 'NOT_FOUND' | 'IN_USE' | 'ALREADY_HIDDEN' | 'UNKNOWN';
}

const handleVendorApiError = (e: unknown): VendorActionResponse | null => {
    if (e && typeof e === 'object' && ('stat' in e || 'errCode' in e)) {
        return e as VendorActionResponse;
    }
    if (e && typeof e === 'object' && 'response' in e) {
        const response = (e as { response?: { data?: VendorActionResponse } }).response;
        if (response?.data) return response.data;
    }
    console.error(e);
    return null;
};

export const getList = async (getData: ProductListParams): Promise<GetProductListResultType | null> => {
    try {
        const response = await axios('/products', { params: getData });
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getProduct = async (productId: number | string): Promise<ProductType | null> => {
    try {
        const response = await axios(`/products/${productId}`);
        return response.data;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const setStat = async (aId: number | number[], status: number): Promise<boolean> => {
    try {
        const response = await axios.post('/products/status', { aId, status });
        return response.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const save = async (
    productId: number | string,
    saveData: ProductCreateDataType & ProductSaveDataType,
): Promise<ProductSaveResponse | false> => {
    try {
        const response = await axios.post(`/products/${productId}`, saveData);
        return response.data || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const saveImages = async (productId: number | string, prodImages: string): Promise<boolean> => {
    try {
        const response = await axios.post(`/products/${productId}/images`, { prodImages });
        return response.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const saveFiles = async (productId: number | string, prodFiles: string): Promise<boolean> => {
    try {
        const response = await axios.post(`/products/${productId}/files`, { prodFiles });
        return response.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const saveVendor = async (productId: number | string, vendorId: number | null): Promise<boolean> => {
    try {
        const response = await axios.post(`/products/${productId}/vendor`, { vendorId });
        return response.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const deleteProduct = async (productId: number | string): Promise<boolean> => {
    try {
        const response = await axios.delete(`/products/${productId}`);
        return response.data?.stat;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const getVendorUsage = async (): Promise<Record<number, number> | null> => {
    try {
        const response = await axios.get('/products/vendors/usage');
        return response.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createVendor = async (vendorName: string): Promise<VendorActionResponse | null> => {
    try {
        const response = await axios.post('/products/vendors/create', { vendorName: vendorName });
        return response.data;
    } catch (e) {
        return handleVendorApiError(e);
    }
};

export const deleteVendor = async (vendorId: number): Promise<VendorActionResponse | null> => {
    try {
        const response = await axios.post(`/products/vendors/delete/${vendorId}`);
        return response.data;
    } catch (e) {
        return handleVendorApiError(e);
    }
};

export const importPreparedProducts = async (items: ProductImportRowType[]): Promise<ProductImportResponseType | null> => {
    try {
        const response = await axios.post('/products/import', { items });
        return response.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};
